import React, { useState, useEffect, useCallback } from 'react';
import {
  History, Clock, RotateCcw, Eye, Trash2, ChevronDown, ChevronUp,
  Loader2, AlertCircle, CheckCircle, X
} from 'lucide-react';
import { agentVersionsAPI } from '../../../services/api';

const VersionHistoryTab = ({ agentId, clientId, onRestore }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [message, setMessage] = useState(null);
  const [previewVersion, setPreviewVersion] = useState(null);

  const fetchVersions = useCallback(async () => {
    if (!agentId) return;
    
    try {
      setLoading(true);
      const response = await agentVersionsAPI.getVersions(clientId, agentId);
      
      if (response.data.success) {
        setVersions(response.data.versions || []);
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
      setMessage({ type: 'error', text: 'Failed to load version history' });
    } finally {
      setLoading(false);
    }
  }, [agentId, clientId]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const handleRestore = async (versionId) => {
    setRestoring(versionId);
    try {
      const response = await agentVersionsAPI.restoreVersion(clientId, agentId, versionId);
      
      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
        if (onRestore) {
          onRestore(response.data.agent);
        }
        fetchVersions();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to restore version' });
    } finally {
      setRestoring(null);
    }
  };

  const handleDelete = async (versionId) => {
    setDeleting(versionId);
    try {
      const response = await agentVersionsAPI.deleteVersion(clientId, agentId, versionId);
      
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Version deleted' });
        fetchVersions();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete version' });
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChangeSummary = (snapshot) => {
    const changes = [];
    if (snapshot?.name) changes.push(`Name: ${snapshot.name}`);
    if (snapshot?.voice_provider) changes.push(`Voice: ${snapshot.voice_provider}`);
    if (snapshot?.llm_model) changes.push(`LLM: ${snapshot.llm_model}`);
    if (snapshot?.script) changes.push('Script modified');
    return changes.slice(0, 3);
  };

  if (!agentId) {
    return (
      <div className="text-center py-12">
        <History className="w-12 h-12 text-gray-600 mx-auto mb-3" />
        <p className="text-gray-400">Save the agent first to enable version history</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="version-history-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <History className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Version History</h3>
            <p className="text-gray-400 text-sm">{total} versions saved (max 20)</p>
          </div>
        </div>
        <button
          onClick={fetchVersions}
          className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg flex items-center gap-2 ${
          message.type === 'error' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
        }`}>
          {message.type === 'error' ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          <span className="text-sm">{message.text}</span>
          <button onClick={() => setMessage(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Version List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
        </div>
      ) : versions.length === 0 ? (
        <div className="text-center py-12 bg-[#1a1a1f] border border-gray-800 rounded-xl">
          <History className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No versions saved yet</p>
          <p className="text-gray-500 text-sm mt-1">
            Versions are automatically created when you save changes
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {versions.map((version, index) => (
            <div
              key={version.id}
              className="bg-[#1a1a1f] border border-gray-800 rounded-xl overflow-hidden"
            >
              {/* Version Header */}
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#252530] transition-colors"
                onClick={() => setExpandedVersion(expandedVersion === version.id ? null : version.id)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    index === 0 ? 'bg-purple-500/30 text-purple-400' : 'bg-gray-800 text-gray-400'
                  }`}>
                    {version.version_number || versions.length - index}
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">
                      {version.change_summary || 'Configuration updated'}
                      {index === 0 && <span className="ml-2 text-xs text-purple-400">(Latest)</span>}
                    </p>
                    <div className="flex items-center gap-2 text-gray-500 text-xs mt-0.5">
                      <Clock className="w-3 h-3" />
                      {formatDate(version.created_at)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); setPreviewVersion(version); }}
                    className="p-2 text-gray-400 hover:text-teal-400 hover:bg-teal-500/10 rounded-lg transition-colors"
                    title="Preview"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRestore(version.id); }}
                    disabled={restoring === version.id}
                    className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                    title="Restore this version"
                    data-testid={`restore-version-${version.id}`}
                  >
                    {restoring === version.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RotateCcw className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(version.id); }}
                    disabled={deleting === version.id}
                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    title="Delete version"
                  >
                    {deleting === version.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                  {expandedVersion === version.id ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedVersion === version.id && version.snapshot && (
                <div className="border-t border-gray-800 p-4 bg-[#0d1117]">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {version.snapshot.name && (
                      <div>
                        <span className="text-gray-500">Name:</span>
                        <span className="text-white ml-2">{version.snapshot.name}</span>
                      </div>
                    )}
                    {version.snapshot.voice_provider && (
                      <div>
                        <span className="text-gray-500">Voice Provider:</span>
                        <span className="text-white ml-2">{version.snapshot.voice_provider}</span>
                      </div>
                    )}
                    {version.snapshot.llm_model && (
                      <div>
                        <span className="text-gray-500">LLM Model:</span>
                        <span className="text-white ml-2">{version.snapshot.llm_model}</span>
                      </div>
                    )}
                    {version.snapshot.language && (
                      <div>
                        <span className="text-gray-500">Language:</span>
                        <span className="text-white ml-2">{version.snapshot.language}</span>
                      </div>
                    )}
                    {version.snapshot.status && (
                      <div>
                        <span className="text-gray-500">Status:</span>
                        <span className={`ml-2 ${version.snapshot.status === 'active' ? 'text-green-400' : 'text-gray-400'}`}>
                          {version.snapshot.status}
                        </span>
                      </div>
                    )}
                  </div>
                  {version.snapshot.script && (
                    <div className="mt-4">
                      <span className="text-gray-500 text-sm">Script Preview:</span>
                      <div className="mt-2 p-3 bg-[#1a1a1f] rounded-lg border border-gray-800 max-h-32 overflow-y-auto">
                        <pre className="text-gray-300 text-xs whitespace-pre-wrap">
                          {version.snapshot.script.substring(0, 500)}
                          {version.snapshot.script.length > 500 && '...'}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {previewVersion && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-[#1a1a1f] border border-gray-800 rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">
                Version {previewVersion.version_number} Preview
              </h3>
              <button onClick={() => setPreviewVersion(null)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-[#0d1117] rounded-lg p-4 border border-gray-800">
                <p className="text-gray-400 text-sm mb-1">Change Summary</p>
                <p className="text-white">{previewVersion.change_summary}</p>
              </div>
              
              <div className="bg-[#0d1117] rounded-lg p-4 border border-gray-800">
                <p className="text-gray-400 text-sm mb-2">Saved Configuration</p>
                <pre className="text-gray-300 text-sm overflow-x-auto">
                  {JSON.stringify(previewVersion.snapshot, null, 2)}
                </pre>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setPreviewVersion(null)}
                className="flex-1 px-4 py-3 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-800"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleRestore(previewVersion.id);
                  setPreviewVersion(null);
                }}
                className="flex-1 px-4 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Restore This Version
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionHistoryTab;
